function readFiniteNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

function readString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readArray(value) {
  return Array.isArray(value) ? value : null;
}

function firstFinite(...values) {
  for (const value of values) {
    const n = readFiniteNumber(value);
    if (n != null) return n;
  }
  return null;
}

function firstString(...values) {
  for (const value of values) {
    const s = readString(value);
    if (s != null) return s;
  }
  return null;
}

function firstArray(...values) {
  for (const value of values) {
    const arr = readArray(value);
    if (arr != null) return arr;
  }
  return null;
}

function findTimeIndexAtOrAfter(times, targetSeconds) {
  if (!Array.isArray(times)) return null;

  for (let index = 0; index < times.length; index += 1) {
    const value = times[index];
    if (typeof value === "number" && Number.isFinite(value) && value >= targetSeconds) {
      return index;
    }
  }

  return null;
}

function findTimeIndexAtOrBefore(times, targetSeconds) {
  if (!Array.isArray(times)) return null;

  for (let index = times.length - 1; index >= 0; index -= 1) {
    const value = times[index];
    if (typeof value === "number" && Number.isFinite(value) && value <= targetSeconds) {
      return index;
    }
  }

  return null;
}

function pickFallbackSpirometrySegment({
  processed,
  results,
  mpm,
  timeSeconds,
  samplingRateHz,
}) {
  const rawSegment = readObject(processed?.spirometrySegment);
  const rawStartIndex = firstFinite(rawSegment?.startIndex);
  const rawEndIndex = firstFinite(rawSegment?.endIndex);

  if (rawStartIndex != null && rawEndIndex != null) {
    return {
      ...rawSegment,
      startIndex: rawStartIndex,
      endIndex: rawEndIndex,
    };
  }

  const spiroQualityMetrics =
    readObject(results?.mpm_spiro_signal_quality?.metrics) ||
    readObject(mpm?.mpm_spiro_signal_quality?.metrics) ||
    null;

  const spirometryMetrics =
    readObject(mpm?.spirometry?.metrics) ||
    readObject(results?.spirometry?.metrics) ||
    null;

  const segmentStartIndex = firstFinite(
    spiroQualityMetrics?.segmentStartIndex,
    spirometryMetrics?.segmentStartIndex,
  );
  const segmentEndIndex = firstFinite(
    spiroQualityMetrics?.segmentEndIndex,
    spirometryMetrics?.segmentEndIndex,
  );

  if (segmentStartIndex != null && segmentEndIndex != null) {
    return {
      ...rawSegment,
      startIndex: segmentStartIndex,
      endIndex: segmentEndIndex,
      source: "quality-metrics-index",
    };
  }

  const signalQualityMetrics =
    readObject(results?.mpm_signal_quality?.metrics) ||
    readObject(mpm?.mpm_signal_quality?.metrics) ||
    null;

  const segmentStartSeconds = firstFinite(
    signalQualityMetrics?.spirometrySegmentStartSeconds,
    spiroQualityMetrics?.segmentStartSeconds,
    spirometryMetrics?.spirometrySegmentStartSeconds,
  );
  const segmentEndSeconds = firstFinite(
    signalQualityMetrics?.spirometrySegmentEndSeconds,
    spiroQualityMetrics?.segmentEndSeconds,
    spirometryMetrics?.spirometrySegmentEndSeconds,
  );

  if (segmentStartSeconds == null || segmentEndSeconds == null) {
    return rawSegment || null;
  }

  let startIndex = findTimeIndexAtOrAfter(timeSeconds, segmentStartSeconds);
  let endIndex = findTimeIndexAtOrBefore(timeSeconds, segmentEndSeconds);

  if (
    (startIndex == null || endIndex == null) &&
    typeof samplingRateHz === "number" &&
    Number.isFinite(samplingRateHz) &&
    samplingRateHz > 0
  ) {
    startIndex =
      startIndex ?? Math.max(0, Math.round(segmentStartSeconds * samplingRateHz));
    endIndex =
      endIndex ?? Math.max(startIndex, Math.round(segmentEndSeconds * samplingRateHz));
  }

  if (startIndex == null || endIndex == null) {
    return rawSegment || null;
  }

  return {
    ...rawSegment,
    startIndex,
    endIndex,
    startTimeSeconds: segmentStartSeconds,
    endTimeSeconds: segmentEndSeconds,
    source: "quality-metrics-seconds",
  };
}

function toTimeValuePoints(values, times, samplingRateHz = 30) {
  if (!Array.isArray(values)) return [];

  return values
    .map((value, index) => {
      if (typeof value !== "number" || !Number.isFinite(value)) return null;

      const t =
        Array.isArray(times) &&
        typeof times[index] === "number" &&
        Number.isFinite(times[index])
          ? times[index]
          : index / samplingRateHz;

      return { t, value, index };
    })
    .filter(Boolean);
}

export function normalizeSpirometryMetrics({ fev1, pef }) {
  const zeroedMetrics = fev1 === 0 && pef === 0;

  if (!zeroedMetrics) return { fev1, pef, zeroedMetrics: false };

  return {
    fev1: null,
    pef: null,
    zeroedMetrics: true,
  };
}

function normalizeQualityStatus(value) {
  const raw = readString(value);
  if (!raw) return null;

  const lowered = raw.trim().toLowerCase();
  return lowered === "pass" ? "pass" : "fail";
}

function buildQualityDisplay(status, rawStatus) {
  const normalized = normalizeQualityStatus(status ?? rawStatus);
  const raw = readString(rawStatus ?? status);

  return {
    status: normalized,
    rawStatus: raw,
    isPass: normalized === "pass",
    isFail: normalized === "fail",
  };
}

function pickLegacySpirometryResults(docData) {
  return docData?.results || docData?.spiro?.results || null;
}

function pickMpmProcessedSignal(docData) {
  const processed =
    docData?.["mpm-signal-processed"] ||
    docData?.mpmSignalProcessed ||
    docData?.mpm_signal_processed ||
    null;
  const results = docData?.results || null;
  const mpm = results?.mpm || null;

  if (!processed) {
    return {
      samplingRateHz: null,

      detrendedSignal: [],
      spirometryDetrendedSignal: [],
      signalTrace: [],
      spirometrySignalTrace: [],

      detrendedSignalPoints: [],
      spirometryDetrendedSignalPoints: [],
      signalTracePoints: [],
      spirometrySignalTracePoints: [],

      preSpirometryDetrendedSignal: [],
      postSpirometryDetrendedSignal: [],
      preSpirometryDetrendedSignalPoints: [],
      postSpirometryDetrendedSignalPoints: [],

      timeSeconds: [],
      spirometryTimeSeconds: [],
      preSpirometryTimeSeconds: [],
      postSpirometryTimeSeconds: [],
      spirometrySegment: null,

      flowLoop: [],
    };
  }

  const timeSeconds = firstArray(
    processed.resampledTimesSeconds,
    processed.timeSeconds,
    processed.time_seconds,
    processed.timestamps,
    processed.t,
  );

  const detrendedSignal = firstArray(
    processed.detrendedTrace,
    processed.detrendedSignal,
    processed.detrended_signal,
    processed.detrended,
    processed.signalDetrended,
    processed.signal_detrended,
  );

  const signalTrace = firstArray(
    processed.smoothedTrace,
    processed.smoothed_signal,
    processed.smoothed,
    processed.resampledTrace,
    processed.trace,
    processed.signalTrace,
    processed.signal_trace,
    processed.processedSignal,
    processed.processed_signal,
    processed.signal,
    processed.values,
    processed.detrendedTrace,
  );

  const samplingRateHz = firstFinite(
    processed.samplingRateHz,
    processed.sampling_rate_hz,
    processed.samplingRate,
    processed.sampling_rate,
    30,
  );

  const spirometrySegment = pickFallbackSpirometrySegment({
    processed,
    results,
    mpm,
    timeSeconds,
    samplingRateHz,
  });

  const startIndex = readFiniteNumber(spirometrySegment?.startIndex);
  const endIndex = readFiniteNumber(spirometrySegment?.endIndex);
  const hasSegment = startIndex != null && endIndex != null;

  const safeStartIndex = hasSegment
    ? Math.max(0, Math.floor(startIndex))
    : null;
  const safeEndIndex = hasSegment
    ? Math.max(safeStartIndex, Math.floor(endIndex))
    : null;

  const spirometryTimeSeconds =
    timeSeconds && hasSegment
      ? timeSeconds.slice(safeStartIndex, safeEndIndex + 1)
      : timeSeconds || [];

  const spirometrySignalTrace =
    signalTrace && hasSegment
      ? signalTrace.slice(safeStartIndex, safeEndIndex + 1)
      : signalTrace || [];

  const spirometryDetrendedSignal =
    detrendedSignal && hasSegment
      ? detrendedSignal.slice(safeStartIndex, safeEndIndex + 1)
      : detrendedSignal || [];

  const preSpirometryTimeSeconds =
    timeSeconds && hasSegment
      ? timeSeconds.slice(0, safeStartIndex)
      : timeSeconds || [];

  const postSpirometryTimeSeconds =
    timeSeconds && hasSegment ? timeSeconds.slice(safeEndIndex + 1) : [];

  const preSpirometryDetrendedSignal =
    detrendedSignal && hasSegment
      ? detrendedSignal.slice(0, safeStartIndex)
      : detrendedSignal || [];

  const postSpirometryDetrendedSignal =
    detrendedSignal && hasSegment
      ? detrendedSignal.slice(safeEndIndex + 1)
      : [];

  return {
    samplingRateHz,

    detrendedSignal: detrendedSignal || [],
    spirometryDetrendedSignal,
    signalTrace: signalTrace || [],
    spirometrySignalTrace,

    detrendedSignalPoints: toTimeValuePoints(
      detrendedSignal || [],
      timeSeconds || [],
      samplingRateHz,
    ),

    spirometryDetrendedSignalPoints: toTimeValuePoints(
      spirometryDetrendedSignal,
      spirometryTimeSeconds,
      samplingRateHz,
    ),

    signalTracePoints: toTimeValuePoints(
      signalTrace || [],
      timeSeconds || [],
      samplingRateHz,
    ),

    spirometrySignalTracePoints: toTimeValuePoints(
      spirometrySignalTrace,
      spirometryTimeSeconds,
      samplingRateHz,
    ),

    preSpirometryDetrendedSignal,
    postSpirometryDetrendedSignal,

    preSpirometryDetrendedSignalPoints: toTimeValuePoints(
      preSpirometryDetrendedSignal,
      preSpirometryTimeSeconds,
      samplingRateHz,
    ),

    postSpirometryDetrendedSignalPoints: toTimeValuePoints(
      postSpirometryDetrendedSignal,
      postSpirometryTimeSeconds,
      samplingRateHz,
    ),

    timeSeconds: timeSeconds || [],
    spirometryTimeSeconds,
    preSpirometryTimeSeconds,
    postSpirometryTimeSeconds,
    spirometrySegment,

    flowLoopBreaths: [],
    flowLoop: [],
    spirometryFlowLoop: [],
  };
}

function readObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : null;
}

function buildFlowLoopBreathPoints(breaths) {
  if (!Array.isArray(breaths)) return [];

  return breaths
    .map((breath) => {
      const plotData = readObject(breath?.plotData);
      const volumeTrace = Array.isArray(plotData?.volumeTrace)
        ? plotData.volumeTrace
        : [];
      const flowTrace = Array.isArray(plotData?.flowTrace)
        ? plotData.flowTrace
        : [];

      const n = Math.min(volumeTrace.length, flowTrace.length);
      if (n < 2) return null;

      const points = [];
      for (let i = 0; i < n; i += 1) {
        const x = volumeTrace[i];
        const y = flowTrace[i];

        if (
          typeof x !== "number" ||
          !Number.isFinite(x) ||
          typeof y !== "number" ||
          !Number.isFinite(y)
        ) {
          continue;
        }

        points.push({ x, y });
      }

      if (points.length < 2) return null;

      return {
        breathNumber: readFiniteNumber(breath?.breathNumber),
        isSpirometryManeuverBreath:
          breath?.metrics?.isSpirometryManeuverBreath === true,
        points,
      };
    })
    .filter(Boolean);
}

function buildCombinedFlowLoopPoints(
  breathLoops,
  {
    hideSpirometryManeuverBreaths = false,
    onlySpirometryManeuverBreaths = false,
  } = {},
) {
  if (!Array.isArray(breathLoops)) return [];

  return breathLoops.flatMap((breath) => {
    const isSpiro = breath?.isSpirometryManeuverBreath === true;

    if (onlySpirometryManeuverBreaths && !isSpiro) return [];
    if (hideSpirometryManeuverBreaths && isSpiro) return [];

    return Array.isArray(breath?.points) ? breath.points : [];
  });
}

export const SPIROMETRY_PREDICTION_SOURCES = {
  LUNGDX: "lungdx",
  GLI: "gli",
};

function normalizePredictionSource(source) {
  return source === SPIROMETRY_PREDICTION_SOURCES.GLI
    ? SPIROMETRY_PREDICTION_SOURCES.GLI
    : SPIROMETRY_PREDICTION_SOURCES.LUNGDX;
}

function pickPredictionSourceMetric(metrics, source, lungDxKeys, gliKeys) {
  const normalizedSource = normalizePredictionSource(source);

  const selectedKeys =
    normalizedSource === SPIROMETRY_PREDICTION_SOURCES.GLI
      ? gliKeys
      : lungDxKeys;

  return firstFinite(...selectedKeys.map((key) => metrics?.[key]));
}

export function pickSpirometryResult(
  docData,
  { predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX } = {},
) {
  const legacy = pickLegacySpirometryResults(docData);
  const results = docData?.results || null;
  const mpm = results?.mpm || null;

  const respiratoryRate = mpm?.respiratory_rate || null;
  const flowLoop = mpm?.flow_loop || null;
  const flowLoopSummary = flowLoop?.summary_metrics || null;

  const spirometry = mpm?.spirometry || null;
  const spirometryMetrics = readObject(spirometry?.metrics) || {};

  const signalQualityDoc =
    results?.mpm_signal_quality || mpm?.mpm_signal_quality || null;
  const spiroSignalQualityDoc =
    results?.mpm_spiro_signal_quality || mpm?.mpm_spiro_signal_quality || null;

  const signalQualityRaw = firstString(
    legacy?.Signal_Quality,
    legacy?.signal_quality,
    signalQualityDoc?.status,
    flowLoop?.quality_status,
    flowLoop?.quality,
    respiratoryRate?.qualityStatus,
    respiratoryRate?.quality,
  );

  const spirometryQualityRaw = firstString(spiroSignalQualityDoc?.status);

  const signalQualityInfo = buildQualityDisplay(
    signalQualityRaw,
    signalQualityRaw,
  );
  const spirometryQualityInfo = buildQualityDisplay(
    spirometryQualityRaw,
    spirometryQualityRaw,
  );

  const signalQuality = signalQualityInfo.status;
  const spirometryQuality = spirometryQualityInfo.status;

  const rr = {
    average: firstFinite(
      respiratoryRate?.averageBreathsPerMinute,
      respiratoryRate?.rr_avg,
      mpm?.rr_avg,
    ),
    mean: firstFinite(
      respiratoryRate?.meanBreathsPerMinute,
      respiratoryRate?.rr_mean,
      mpm?.rr_mean,
    ),
    median: firstFinite(
      respiratoryRate?.medianBreathsPerMinute,
      respiratoryRate?.rr_median,
      mpm?.rr_median,
    ),
    min: firstFinite(
      respiratoryRate?.minBreathsPerMinute,
      respiratoryRate?.rr_min,
      mpm?.rr_min,
    ),
    max: firstFinite(
      respiratoryRate?.maxBreathsPerMinute,
      respiratoryRate?.rr_max,
      mpm?.rr_max,
    ),
    breathCount: firstFinite(
      respiratoryRate?.breathCount,
      flowLoop?.breath_count,
      flowLoopSummary?.spirometryBreathCount,
    ),
  };

  const additionalMetrics = {
    meanExhaleInhaleAreaRatio: readFiniteNumber(
      flowLoopSummary?.meanExhaleInhaleAreaRatio,
    ),
    meanRectangularAreaRatio: readFiniteNumber(
      flowLoopSummary?.meanRectangularAreaRatio,
    ),
    minBreathDurationSeconds: readFiniteNumber(
      flowLoopSummary?.minBreathDurationSeconds,
    ),
    maxBreathDurationSeconds: readFiniteNumber(
      flowLoopSummary?.maxBreathDurationSeconds,
    ),
    meanBreathDurationSeconds: readFiniteNumber(
      flowLoopSummary?.meanBreathDurationSeconds,
    ),
    intervalCv: readFiniteNumber(
      respiratoryRate?.metrics?.intervalCoefficientOfVariation,
    ),
    intervalMeanSeconds: readFiniteNumber(
      respiratoryRate?.metrics?.intervalMeanSeconds,
    ),
    intervalMedianSeconds: readFiniteNumber(
      respiratoryRate?.metrics?.intervalMedianSeconds,
    ),
  };

  const plots = {
    respiratoryRatePlotUri: readString(respiratoryRate?.plot_storage_uri),
    flowLoopPlotUri: readString(flowLoop?.plot_storage_uri),
  };

  const processedSignal = pickMpmProcessedSignal(docData);

  const firestoreBreaths = Array.isArray(flowLoop?.breaths)
    ? flowLoop.breaths
    : [];
  const firestoreBreathLoops = buildFlowLoopBreathPoints(firestoreBreaths);

  const normalizedPredictionSource =
    normalizePredictionSource(predictionSource);

  const lungDxPredictedFev1 = firstFinite(
    spirometry?.fev1,
    spirometryMetrics?.predictedFev1,
    spirometryMetrics?.predictedFEV1,
    spirometryMetrics?.lungDxPredictedFev1,
    spirometryMetrics?.lungdxPredictedFev1,
    spirometryMetrics?.fev1,
    spirometryMetrics?.FEV1,
  );

  const gliPredictedFev1 = firstFinite(
    spirometry?.gli_fev1,
    spirometryMetrics?.gliPredictedFev1,
    spirometryMetrics?.gliPredictedFEV1,
  );

  const lungDxPredictedPef = firstFinite(
    spirometry?.pef,
    spirometryMetrics?.predictedPef,
    spirometryMetrics?.predictedPEF,
    spirometryMetrics?.lungDxPredictedPef,
    spirometryMetrics?.lungdxPredictedPef,
    spirometryMetrics?.lungDxPredictedPEF,
    spirometryMetrics?.lungdxPredictedPEF,
    spirometryMetrics?.pef,
    spirometryMetrics?.PEF,
  );

  const gliPredictedPef = firstFinite(
    spirometry?.gli_pef,
    spirometryMetrics?.gliPredictedPef,
    spirometryMetrics?.gliPredictedPEF,
  );

  const selectedPredictedFev1 = firstFinite(
    normalizedPredictionSource === SPIROMETRY_PREDICTION_SOURCES.LUNGDX
      ? spirometry?.fev1
      : null,
    pickPredictionSourceMetric(
      spirometryMetrics,
      normalizedPredictionSource,
      [
        "predictedFev1",
        "predictedFEV1",
        "lungDxPredictedFev1",
        "lungdxPredictedFev1",
        "fev1",
        "FEV1",
      ],
      ["gliPredictedFev1", "gliPredictedFEV1"],
    ),
  );

  const selectedPredictedPef = firstFinite(
    normalizedPredictionSource === SPIROMETRY_PREDICTION_SOURCES.LUNGDX
      ? spirometry?.pef
      : null,
    pickPredictionSourceMetric(
      spirometryMetrics,
      normalizedPredictionSource,
      [
        "predictedPef",
        "predictedPEF",
        "lungDxPredictedPef",
        "lungdxPredictedPef",
        "lungDxPredictedPEF",
        "lungdxPredictedPEF",
        "pef",
        "PEF",
      ],
      ["gliPredictedPef", "gliPredictedPEF"],
    ),
  );

  const lungDxPredictedFvc = firstFinite(
    spirometry?.fvc,
    spirometryMetrics?.predictedFvc,
    spirometryMetrics?.predictedFVC,
    spirometryMetrics?.lungDxPredictedFvc,
    spirometryMetrics?.lungdxPredictedFvc,
    spirometryMetrics?.lungDxPredictedFVC,
    spirometryMetrics?.lungdxPredictedFVC,
    spirometryMetrics?.fvc,
    spirometryMetrics?.FVC,
  );

  const gliPredictedFvc = firstFinite(
    spirometry?.fvc,
    spirometryMetrics?.gliPredictedFvc,
    spirometryMetrics?.gliPredictedFVC,
  );

  const selectedPredictedFvc = firstFinite(
    normalizedPredictionSource === SPIROMETRY_PREDICTION_SOURCES.LUNGDX
      ? spirometry?.fvc
      : null,
    pickPredictionSourceMetric(
      spirometryMetrics,
      normalizedPredictionSource,
      [
        "predictedFvc",
        "predictedFVC",
        "lungDxPredictedFvc",
        "lungdxPredictedFvc",
        "lungDxPredictedFVC",
        "lungdxPredictedFVC",
        "fvc",
        "FVC",
      ],
      ["gliPredictedFvc", "gliPredictedFVC"],
    ),
  );

  const lungDxPredictedFev1FvcRatio = firstFinite(
    spirometryMetrics?.predictedFev1FvcRatio,
    spirometryMetrics?.predictedFEV1FVCRatio,
    spirometryMetrics?.lungDxPredictedFev1FvcRatio,
    spirometryMetrics?.lungdxPredictedFev1FvcRatio,
    spirometryMetrics?.fev1FvcRatio,
    spirometryMetrics?.FEV1FVCRatio,
  );

  const gliPredictedFev1FvcRatio = firstFinite(
    spirometryMetrics?.gliPredictedFev1FvcRatio,
    spirometryMetrics?.gliPredictedFEV1FVCRatio,
  );

  const selectedPredictedFev1FvcRatio = pickPredictionSourceMetric(
    spirometryMetrics,
    normalizedPredictionSource,
    [
      "predictedFev1FvcRatio",
      "predictedFEV1FVCRatio",
      "lungDxPredictedFev1FvcRatio",
      "lungdxPredictedFev1FvcRatio",
      "fev1FvcRatio",
      "FEV1FVCRatio",
    ],
    ["gliPredictedFev1FvcRatio", "gliPredictedFEV1FVCRatio"],
  );

  const predicted = {
    source: normalizedPredictionSource,
    fev1: readFiniteNumber(selectedPredictedFev1),
    fvc: readFiniteNumber(selectedPredictedFvc),
    fev1FvcRatio: readFiniteNumber(selectedPredictedFev1FvcRatio),
    pef: readFiniteNumber(selectedPredictedPef),

    raw: {
      lungdx: {
        fev1: readFiniteNumber(lungDxPredictedFev1),
        fvc: readFiniteNumber(lungDxPredictedFvc),
        fev1FvcRatio: readFiniteNumber(lungDxPredictedFev1FvcRatio),
        pef: readFiniteNumber(lungDxPredictedPef),
      },
      gli: {
        fev1: readFiniteNumber(gliPredictedFev1),
        fvc: readFiniteNumber(gliPredictedFvc),
        fev1FvcRatio: readFiniteNumber(gliPredictedFev1FvcRatio),
        pef: readFiniteNumber(gliPredictedPef),
      },
    },
  };

  return {
    ...normalizeSpirometryMetrics({
      fev1: predicted.fev1,
      pef: predicted.pef,
    }),

    fvc: predicted.fvc,
    fev1FvcRatio: predicted.fev1FvcRatio,

    predicted,

    rr,
    signalQuality,
    signalQualityRaw,
    signalQualityChecks: signalQualityDoc?.checks || null,
    signalQualityMetrics: signalQualityDoc?.metrics || null,

    spirometryQuality,
    spirometryQualityRaw,
    spirometryQualityChecks: spiroSignalQualityDoc?.checks || null,
    spirometryQualityMetrics: spiroSignalQualityDoc?.metrics || null,

    additionalMetrics,
    plots,
    processedSignal: {
      ...processedSignal,
      flowLoopBreaths: firestoreBreathLoops,
      flowLoop: buildCombinedFlowLoopPoints(firestoreBreathLoops, {
        hideSpirometryManeuverBreaths: true,
      }),
      spirometryFlowLoop: buildCombinedFlowLoopPoints(firestoreBreathLoops, {
        onlySpirometryManeuverBreaths: true,
      }),
    },
  };
}

export function isSpirometryFailure({
  signalQuality,
  spirometryQuality,
  zeroedMetrics,
}) {
  return (
    (typeof signalQuality === "string" && signalQuality !== "pass") ||
    (typeof spirometryQuality === "string" && spirometryQuality !== "pass") ||
    zeroedMetrics === true
  );
}
